import React, { useState, useRef, useEffect } from 'react';
import { useParcelContext } from '../context/ParcelContext';
import styled from 'styled-components';
import { FaFilter, FaSearch, FaTimes, FaExclamationTriangle, FaMapMarkedAlt } from 'react-icons/fa';
import Select from 'react-select';
import { getTilesetUrlForJurisdiction } from '../utils/tilesetUtils';

// Utility functions for proper capitalization
const capitalizeStateCode = (stateCode) => {
  return stateCode ? stateCode.toUpperCase() : '';
};

const capitalizeName = (name) => {
  if (!name) return '';
  return name.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
};

const FilterContainer = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #e0e0e0;
  background: white;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const FilterContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: 0.5rem; /* Space for scrollbar */
`;

const FilterActions = styled.div`
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
  background: white;
`;



const FilterHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #667eea;
`;

const FilterTitle = styled.h3`
  margin: 0;
  color: #333;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FilterTypeToggle = styled.div`
  display: flex;
  margin-bottom: 1rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
`;

const ToggleButton = styled.button`
  flex: 1;
  padding: 0.5rem;
  border: none;
  background: ${props => props.active ? '#667eea' : '#f5f5f5'};
  color: ${props => props.active ? 'white' : '#333'};
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? '#5a6fd8' : '#e9e9e9'};
  }
`;

const FilterModeToggle = styled.div`
  display: flex;
  margin-bottom: 1rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
  }
`;

const SearchButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: background-color 0.2s;
  
  &:hover {
    background: #5a6fd8;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ClearButton = styled.button`
  width: 100%;
  padding: 0.5rem;
  background: #f5f5f5;
  color: #666;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  
  &:hover {
    background: #e9e9e9;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Modal styles for Query Runtime Exceeded
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e0e0e0;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const AlertBox = styled.div`
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  color: #856404;
`;

const SuggestionsList = styled.ul`
  margin: 1rem 0;
  padding-left: 1.5rem;
  
  li {
    margin-bottom: 0.5rem;
    color: #666;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
  
  &.primary {
    background: #667eea;
    color: white;
    
    &:hover {
      background: #5a6fd8;
    }
  }
  
  &.secondary {
    background: #6c757d;
    color: white;
    
    &:hover {
      background: #5a6268;
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FilterPanel = ({ onGetMapBounds }) => {
  const {
    filters,
    states,
    counties,
    futureLandUseOptions,
    inPlaceZoningOptions,
    loading,
    updateFilters,
    searchParcels,
    retrySearchWithBbox,
    cancelQueryRuntimeExceeded,
    cancelQueryRejected,
    queryRuntimeExceeded,
    queryRejected,
    pendingSearchWithBbox,
    setTilesetUrl,
    filterQueryInProgress
  } = useParcelContext();

  const [localFilters, setLocalFilters] = useState(filters);

  const handleFilterChange = (field, value) => {
    const newFilters = { ...localFilters, [field]: value };
    
    setLocalFilters(newFilters);
    updateFilters(newFilters);
  };

  const handleSearch = () => {
    searchParcels();
  };

  const handleClear = () => {
    const clearedFilters = {
      state: '',
      county: '',
      futureLandUse: '',
      futureLandUseMulti: [],
      inPlaceZoning: '',
      minAcreage: '',
      maxAcreage: '',
      maxAssessedValuePerAcre: '',
      filterType: 'single',
      filterMode: 'futureLandUse'
    };
    setLocalFilters(clearedFilters);
    updateFilters(clearedFilters);
  };

  // Load and set tileset URL when state and county are set
  useEffect(() => {
    const loadTileset = async () => {
      if (localFilters.state && localFilters.county) {
        const url = await getTilesetUrlForJurisdiction(localFilters.state, localFilters.county);
        setTilesetUrl(url);
      } else {
        setTilesetUrl('');
      }
    };
    loadTileset();
  }, [localFilters.state, localFilters.county, setTilesetUrl]);

  const stateOptions = states.map(state => ({
    value: state,
    label: capitalizeName(state)
  }));

  // Filter counties based on current filter mode and data availability
  const getFilteredCounties = () => {
    if (!Array.isArray(counties)) return [];
    
    return counties.filter(county => {
      if (localFilters.filterMode === 'futureLandUse') {
        return county.has_flu_data === true;
      } else if (localFilters.filterMode === 'inPlaceZoning') {
        return county.has_zoning_data === true;
      }
      return true; // Show all counties if no specific filter mode
    });
  };

  const filteredCounties = getFilteredCounties();

  const countyOptions = filteredCounties.map(county => ({
    value: county.county_name,
    label: capitalizeName(county.county_name)
  }));

  const futureLandUseOptionsFormatted = futureLandUseOptions.map(option => ({
    value: option,
    label: option
  }));

  const inPlaceZoningOptionsFormatted = inPlaceZoningOptions.map(option => ({
    value: option,
    label: option
  }));

  return (
    <FilterContainer>
      <FilterHeader>
        <FilterTitle>
          <FaFilter />
          Search Filters
        </FilterTitle>
      </FilterHeader>

      <FilterContent>
        {/* Filter Type Toggle */}
        <FilterTypeToggle>
          <ToggleButton
            active={localFilters.filterType === 'single'}
            onClick={() => handleFilterChange('filterType', 'single')}
          >
            Single Parcel
          </ToggleButton>
          <ToggleButton
            active={localFilters.filterType === 'multi'}
            onClick={() => handleFilterChange('filterType', 'multi')}
          >
            Multi Parcel
          </ToggleButton>
        </FilterTypeToggle>

        {/* Filter Mode Toggle */}
        <FilterModeToggle>
          <ToggleButton
            active={localFilters.filterMode === 'futureLandUse'}
            onClick={() => handleFilterChange('filterMode', 'futureLandUse')}
          >
            Future Land Use
          </ToggleButton>
          <ToggleButton
            active={localFilters.filterMode === 'inPlaceZoning'}
            onClick={() => handleFilterChange('filterMode', 'inPlaceZoning')}
          >
            In-Place Zoning
          </ToggleButton>
        </FilterModeToggle>

        {/* State Selection */}
        <FormGroup>
          <Label>State *</Label>
          <Select
            value={stateOptions.find(option => option.value === localFilters.state)}
            onChange={(option) => handleFilterChange('state', option?.value || '')}
            options={stateOptions}
            placeholder="Select State"
            isClearable
          />
        </FormGroup>

        {/* County Selection */}
        <FormGroup>
          <Label>County/City *</Label>
          <Select
            value={countyOptions.find(option => option.value === localFilters.county)}
            onChange={(option) => handleFilterChange('county', option?.value || '')}
            options={countyOptions}
            placeholder="Select County/City"
            isClearable
            isDisabled={!localFilters.state}
          />
        </FormGroup>

        {/* Land Use Designation */}
        {localFilters.filterMode === 'futureLandUse' ? (
          <FormGroup>
            <Label>Future Land Use Designations * (Select Multiple)</Label>
            <Select
              isMulti
              value={localFilters.futureLandUseMulti.map(value => 
                futureLandUseOptionsFormatted.find(option => option.value === value)
              ).filter(Boolean)}
              onChange={(selectedOptions) => {
                const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
                handleFilterChange('futureLandUseMulti', values);
              }}
              options={futureLandUseOptionsFormatted}
              placeholder="Select Future Land Use Designations"
              isClearable
              isDisabled={!localFilters.county}
            />
          </FormGroup>
        ) : (
          <FormGroup>
            <Label>In-Place Zoning *</Label>
            <Select
              value={inPlaceZoningOptionsFormatted.find(option => option.value === localFilters.inPlaceZoning)}
              onChange={(option) => handleFilterChange('inPlaceZoning', option?.value || '')}
              options={inPlaceZoningOptionsFormatted}
              placeholder="Select In-Place Zoning"
              isClearable
              isDisabled={!localFilters.county}
            />
          </FormGroup>
        )}

        {/* Acreage Range */}
        <FormGroup>
          <Label>Min Acreage</Label>
          <Input
            type="number"
            value={localFilters.minAcreage}
            onChange={(e) => handleFilterChange('minAcreage', e.target.value)}
            placeholder="Minimum acreage"
            min="0"
            step="0.01"
          />
        </FormGroup>

        <FormGroup>
          <Label>Max Acreage</Label>
          <Input
            type="number"
            value={localFilters.maxAcreage}
            onChange={(e) => handleFilterChange('maxAcreage', e.target.value)}
            placeholder="Maximum acreage"
            min="0"
            step="0.01"
          />
        </FormGroup>

        {/* Max Assessed Value Per Acre */}
        <FormGroup>
          <Label>Max Assessed Value of Improvements Per Acre</Label>
          <Input
            type="number"
            value={localFilters.maxAssessedValuePerAcre}
            onChange={(e) => handleFilterChange('maxAssessedValuePerAcre', e.target.value)}
            placeholder="Maximum value per acre"
            min="0"
            step="0.01"
          />
        </FormGroup>


      </FilterContent>

      <FilterActions>
        {/* Search Button */}
        <SearchButton
          onClick={handleSearch}
          disabled={loading || filterQueryInProgress || !localFilters.state || !localFilters.county || 
                  (localFilters.filterMode === 'futureLandUse' && 
                  (!localFilters.futureLandUseMulti || localFilters.futureLandUseMulti.length === 0)) ||
                  (localFilters.filterMode === 'inPlaceZoning' && !localFilters.inPlaceZoning)}
        >
          {(loading || filterQueryInProgress) ? (
            <>
              <LoadingSpinner />
              Searching...
            </>
          ) : (
            <>
              <FaSearch />
              Search Parcels
            </>
          )}
        </SearchButton>

        {/* Clear Button */}
        <ClearButton onClick={handleClear}>
          <FaTimes />
          Clear Filters
          </ClearButton>
      </FilterActions>
      
      {/* Query Rejected Modal (estimated runtime too high) */}
      {queryRejected && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                <FaExclamationTriangle />
                Query Rejected
              </ModalTitle>
              <CloseButton onClick={cancelQueryRejected}>&times;</CloseButton>
            </ModalHeader>
            
            <AlertBox>
              <p><strong>{queryRejected.message}</strong></p>
            </AlertBox>
            
            {queryRejected.suggestions && queryRejected.suggestions.length > 0 && (
              <div>
                <p><strong>Suggestions to reduce query complexity:</strong></p>
                <SuggestionsList>
                  {queryRejected.suggestions.map((suggestion, index) => (
                    <li key={index}>{suggestion}</li>
                  ))}
                </SuggestionsList>
              </div>
            )}
            
            <p><strong>Would you like to start the query using the current map view's bounding box?</strong></p>
            <p>This will limit the search to only the area currently visible on the map, which should reduce query complexity and allow the query to proceed.</p>
            
            <ButtonGroup>
              <Button 
                className="secondary" 
                onClick={cancelQueryRejected}
              >
                <FaTimes />
                Cancel
              </Button>
              <Button 
                className="primary" 
                onClick={async () => {
                  if (onGetMapBounds) {
                    const bbox = onGetMapBounds();
                    if (bbox) {
                      // Close the modal first
                      cancelQueryRejected();
                      // Retry with bbox
                      await retrySearchWithBbox(bbox);
                    } else {
                      alert('Unable to get map bounds. Please try again.');
                    }
                  } else {
                    alert('Map bounds not available. Please try again.');
                  }
                }}
                disabled={pendingSearchWithBbox || filterQueryInProgress}
              >
                {pendingSearchWithBbox || filterQueryInProgress ? (
                  <LoadingSpinner />
                ) : (
                  <FaMapMarkedAlt />
                )}
                Yes, Use Current View
              </Button>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
      
      {/* Query Runtime Exceeded Modal (during execution) */}
      {queryRuntimeExceeded && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                <FaExclamationTriangle />
                Query Runtime Exceeded
              </ModalTitle>
              <CloseButton onClick={cancelQueryRuntimeExceeded}>&times;</CloseButton>
            </ModalHeader>
            
            <p><strong>Continue the search in the current map view area:</strong></p>
            <p>This will limit the search to only the area currently visible on the map, which should reduce query complexity.</p>
            
            <ButtonGroup>
              <Button 
                className="secondary" 
                onClick={cancelQueryRuntimeExceeded}
              >
                <FaTimes />
                Cancel Query
              </Button>
              <Button 
                className="primary" 
                onClick={async () => {
                  if (onGetMapBounds) {
                    const bbox = onGetMapBounds();
                    if (bbox) {
                      await retrySearchWithBbox(bbox);
                    } else {
                      alert('Unable to get map bounds. Please try again.');
                    }
                  } else {
                    alert('Map bounds not available. Please try again.');
                  }
                }}
                disabled={pendingSearchWithBbox}
              >
                {pendingSearchWithBbox ? (
                  <LoadingSpinner />
                ) : (
                  <FaMapMarkedAlt />
                )}
                Continue in Current View
              </Button>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
    </FilterContainer>
  );
};

export default FilterPanel; 